import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { getToken, getUser, setAuth } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ProtectedRoute = ({ children }) => {
  const { schoolId: urlSchoolId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const handleAuth = async () => {
      // Check if token is passed via URL (auto-login from ERP)
      const params = new URLSearchParams(location.search);
      const urlToken = params.get('token');

      if (urlToken) {
        // Verify the token with backend and store it
        try {
          const res = await fetch(`${API_URL}/auth/verify`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${urlToken}`,
            },
          });
          const data = await res.json();

          if (res.ok && data.success) {
            const sid = data.schoolId || urlSchoolId;
            setAuth(sid, urlToken, {
              userId: data.userId,
              userName: data.userName,
              userType: data.userType,
              schoolId: sid,
              email: data.email,
            });
            setAuthenticated(true);
            // Remove token from URL (clean URL)
            params.delete('token');
            const cleanSearch = params.toString();
            const cleanPath = location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
            navigate(cleanPath, { replace: true });
          } else {
            setAuthenticated(false);
          }
        } catch {
          setAuthenticated(false);
        }
        setChecking(false);
        return;
      }

      // Normal flow - check localStorage using ERP key format
      if (urlSchoolId) {
        const token = getToken(urlSchoolId);
        const user = getUser(urlSchoolId);
        if (token && user) {
          setAuthenticated(true);
          setChecking(false);
          return;
        }
      }

      setAuthenticated(false);
      setChecking(false);
    };

    handleAuth();
  }, [location.search, navigate, location.pathname, urlSchoolId]);

  if (checking) return null; // Loading state

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  // Validate schoolId in URL matches stored user
  if (urlSchoolId) {
    const user = getUser(urlSchoolId);
    if (user) {
      const userSchoolId = user.schoolId;
      if (userSchoolId && urlSchoolId.toUpperCase() !== userSchoolId.toUpperCase()) {
        const correctPath = location.pathname.replace(`/${urlSchoolId}`, `/${userSchoolId}`);
        return <Navigate to={correctPath} replace />;
      }
    }
  }

  return children;
};

export default ProtectedRoute;
